// src/Games/GamesHooks/BimaPath.jsx
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  WALKTHROUGH_CARDS,
  PERSONAS,
  INCOME_TIERS,
  LIFE_EVENT_OFFSETS,
  EVENT_POOLS,
  fmt,
  premiumPer1M,
  calculatePremium,
  calculateSurrenderValue,
  getActiveEvent,
  generateInsights,
} from './BimaPathData';
import { INITIAL_STATE } from './useBimaGameState';
import '../../styles/Games/BimaPath.css';

// ============================================================
// ROOT COMPONENT
// ============================================================
const BimaPath = () => {
  const [state, setState] = useState({ ...INITIAL_STATE });

  const update = useCallback((patch) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Apply a choice result from an event pool choice
  const applyChoice = useCallback((applyFn) => {
    setState((prev) => {
      const draft = {
        ...prev,
        mmfYearlyContribs: [...prev.mmfYearlyContribs],
        log: [...prev.log],
      };
      const result = applyFn(draft);
      return {
        ...draft,
        pendingStory: {
          story: result.story,
          tone: result.tone || '',
          nextScreen: result.lapsed ? 'lapse_decision' : 'advance_year',
        },
      };
    });
  }, []);

  const reset = useCallback(() => setState({ ...INITIAL_STATE }), []);

  const { screen } = state;

  const screenMap = {
    intro: <IntroScreen update={update} />,
    walkthrough: <WalkthroughScreen state={state} update={update} />,
    persona: <PersonaScreen update={update} />,
    age: <AgeScreen state={state} update={update} />,
    family: <FamilyScreen state={state} update={update} />,
    occupation: <OccupationScreen state={state} update={update} />,
    income: <IncomeScreen state={state} update={update} />,
    health: <HealthScreen state={state} update={update} />,
    product_intro: <ProductIntroScreen state={state} update={update} />,
    product_choice: <ProductChoiceScreen state={state} update={update} />,
    agent: <AgentScreen state={state} update={update} />,
    application: <ApplicationScreen state={state} update={update} />,
    quick_application: <QuickApplicationScreen state={state} update={update} />,
    aml: <AMLScreen state={state} update={update} />,
    aml_consequence: <AMLConsequenceScreen state={state} update={update} />,
    confirmation: <ConfirmationScreen state={state} update={update} />,
    life_events: <LifeEventScreen state={state} update={update} applyChoice={applyChoice} />,
    story_then: <StoryThenScreen state={state} update={update} />,
    lapse_decision: <LapseDecisionScreen state={state} update={update} applyChoice={applyChoice} />,
    ira_choice: <IRAChoiceScreen state={state} update={update} />,
    ira_outcome: <IRAOutcomeScreen state={state} update={update} />,
    end: <EndScreen state={state} reset={reset} />,
  };

  return (
    <div className="bp-game-wrapper">
      <nav className="bp-nav">
        <div className="bp-nav-inner">
          <Link to="/games" className="bp-nav-back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span>Back to Games</span>
          </Link>
          <div className="bp-nav-brand">
            <span>🛡️</span>
            <span>BimaPath</span>
          </div>
        </div>
      </nav>
      <div className="bp-wrap">
        {screenMap[screen] || <IntroScreen update={update} />}
      </div>
    </div>
  );
};

// ============================================================
// INTRO
// ============================================================
const IntroScreen = ({ update }) => {
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  return (
    <>
      <h1 className="bp-display bp-title">Bima <span className="bp-accent">Path</span></h1>
      <p className="bp-tag bp-mono">An insurance choices simulator. Term vs whole life. Built for understanding, not selling.</p>

      <div className="bp-card bp-gold">
        <h2>What this is</h2>
        <p>A 15-minute walk through one person's full insurance journey — picking a product, dealing with an agent, navigating 25 years of life including the moment they try to claim.</p>
        <p>You'll see what an honest agent looks like, what happens when you skip the fine print, and what to do if your insurer treats you unfairly. The numbers and rules are Kenyan-real.</p>
      </div>

      <div className="bp-card">
        <h3>How would you like to start?</h3>
        <p style={{ fontSize: 13, color: 'var(--ink-dim)' }}>Both modes use the same engine. Pick whichever feels right for you today.</p>
        <button className="bp-btn bp-good" style={{ marginTop: 12 }} onClick={() => update({ tourMode: 'quick', walkthroughStep: 0, screen: 'walkthrough' })}>
          <div className="bp-label">🌱 Quick Tour — I'm new to this</div>
          <div className="bp-meta">~8 minutes. We pick a character together and walk through the basics. Less jargon, fewer screens.</div>
        </button>
        <button className="bp-btn" onClick={() => update({ tourMode: 'full', screen: 'age' })}>
          <div className="bp-label">📊 Full Simulation — I want the depth</div>
          <div className="bp-meta">~15 minutes. You set up your own profile, walk through every disclosure and KYC step, see the methodology document.</div>
        </button>
      </div>

      <button className="bp-assumptions-toggle" onClick={() => setAssumptionsOpen(o => !o)}>
        {assumptionsOpen ? '▾ ASSUMPTIONS & METHODOLOGY (collapse)' : '▸ ASSUMPTIONS & METHODOLOGY (for actuarial review)'}
      </button>
      {assumptionsOpen && <AssumptionsDoc />}

      <p className="bp-footer-note">An insurance choices simulator • Educational • Replayable</p>
    </>
  );
};

// ============================================================
// WALKTHROUGH
// ============================================================
const WalkthroughScreen = ({ state, update }) => {
  const { walkthroughStep } = state;
  const total = WALKTHROUGH_CARDS.length;
  const card = WALKTHROUGH_CARDS[walkthroughStep];
  return (
    <>
      <div className="bp-walk-progress">
        {WALKTHROUGH_CARDS.map((_, i) => (
          <div key={i} className={`bp-walk-dot ${i <= walkthroughStep ? 'done' : ''}`} />
        ))}
      </div>
      <div className="bp-walk-card">
        <div className="bp-walk-icon">{card.icon}</div>
        <h2 className="bp-walk-title">{card.title}</h2>
        <div className="bp-walk-body" dangerouslySetInnerHTML={{ __html: card.body }} />
        {card.sub && <div className="bp-walk-sub">{card.sub}</div>}
      </div>
      <div className="bp-walk-nav">
        {walkthroughStep > 0
          ? <button className="bp-btn" style={{ marginBottom: 0, flex: 1 }} onClick={() => update({ walkthroughStep: walkthroughStep - 1 })}>← Back</button>
          : <span style={{ flex: 1 }} />}
        {walkthroughStep < total - 1
          ? <button className="bp-btn bp-primary" style={{ marginBottom: 0, flex: 2 }} onClick={() => update({ walkthroughStep: walkthroughStep + 1 })}>Next ({walkthroughStep + 1} of {total}) →</button>
          : <button className="bp-btn bp-primary" style={{ marginBottom: 0, flex: 2 }} onClick={() => update({ screen: 'persona' })}>Pick your character →</button>}
      </div>
      <p style={{ textAlign: 'center', marginTop: 16 }}>
        <span className="bp-back-link" onClick={() => update({ screen: 'persona' })}>Skip walkthrough →</span>
      </p>
    </>
  );
};

// ============================================================
// PERSONA
// ============================================================
const PersonaScreen = ({ update }) => (
  <>
    <span className="bp-back-link" onClick={() => update({ screen: 'walkthrough', walkthroughStep: WALKTHROUGH_CARDS.length - 1 })}>← back to walkthrough</span>
    <h1 className="bp-display bp-title">Pick a <span className="bp-accent">character</span></h1>
    <p className="bp-tag bp-mono">You'll walk through their next 25 years. Their choices, your hands.</p>
    {Object.entries(PERSONAS).map(([key, p]) => (
      <div key={key} className="bp-persona-card" onClick={() => {
        update({ persona: key, ...p.state, screen: 'product_intro' });
      }}>
        <div className="bp-persona-head">
          <div>
            <div className="bp-persona-name">{p.name}</div>
            <div className="bp-persona-headline">{p.headline}</div>
          </div>
        </div>
        <p className="bp-persona-blurb">{p.blurb}</p>
      </div>
    ))}
    <p className="bp-footer-note">Each character meets the same insurance decisions — but their lives unfold differently.</p>
  </>
);

// ============================================================
// AGE
// ============================================================
const AgeScreen = ({ state, update }) => {
  const { age } = state;
  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'intro' })}>← back</span>
      <h1 className="bp-display bp-title">How <span className="bp-accent">old</span> are you?</h1>
      <p className="bp-tag bp-mono">Your age changes everything — premiums, available products, life events you'll face.</p>
      <div className="bp-age-display">
        <div className="bp-age-num">{age}</div>
        <div className="bp-age-label">Years</div>
      </div>
      <input type="range" min="22" max="65" value={age} className="bp-range"
        onChange={e => update({ age: parseInt(e.target.value) })} />
      <div className="bp-card">
        <h3>What this means for your premiums</h3>
        <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
          Term life (20yr, 5M cover): <strong>KSh {fmt(premiumPer1M(age, 'term') * 5)}/mo</strong><br />
          Whole life (5M cover): <strong>KSh {fmt(premiumPer1M(age, 'whole') * 5)}/mo</strong><br />
          Endowment (20yr, 5M): <strong>KSh {fmt(premiumPer1M(age, 'endow') * 5)}/mo</strong>
        </p>
        <p style={{ fontSize: 12, color: 'var(--ink-dim)', marginTop: 8 }}>Premiums shown assume healthy non-smoker. Real quotes will vary by insurer.</p>
      </div>
      <button className="bp-btn bp-primary" onClick={() => update({ screen: 'family' })}>Continue →</button>
    </>
  );
};

// ============================================================
// FAMILY
// ============================================================
const FamilyScreen = ({ state, update }) => {
  const { maritalStatus, children, dependants } = state;
  const canContinue = maritalStatus && dependants !== null;
  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'age' })}>← back</span>
      <h1 className="bp-display bp-title">Your <span className="bp-accent">family</span></h1>
      <p className="bp-tag bp-mono">Who depends on you financially? This is the core question insurance is answering.</p>

      <div className="bp-card">
        <div className="bp-field-label">Are you married?</div>
        <div className="bp-setup-grid">
          {[['single', 'Single', 'No spouse'], ['married', 'Married', 'Or long-term partner']].map(([v, n, s]) => (
            <div key={v} className={`bp-setup-pick ${maritalStatus === v ? 'sel' : ''}`} onClick={() => update({ maritalStatus: v })}>
              <div className="bp-setup-name">{n}</div>
              <div className="bp-setup-sub">{s}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bp-card">
        <div className="bp-field-label">Number of children</div>
        <div className="bp-setup-grid">
          {[0, 1, 2, 3].map(n => (
            <div key={n} className={`bp-setup-pick ${children === n ? 'sel' : ''}`} onClick={() => update({ children: n })}>
              <div className="bp-setup-name">{n === 0 ? 'None' : n + (n === 1 ? ' child' : ' children')}</div>
              <div className="bp-setup-sub">{n === 0 ? 'No dependents' : n >= 3 ? 'Large family' : 'Growing family'}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bp-card">
        <div className="bp-field-label">Other dependants</div>
        <div className="bp-setup-grid">
          {[
            ['none', 'None', 'Just nuclear family'],
            ['parents', 'Parent(s)', 'Supporting one or both'],
            ['parents_siblings', 'Parents + siblings', 'Multi-generation support'],
            ['extended', 'Extended family', 'Parents, sibs, cousins, etc'],
          ].map(([v, n, s]) => (
            <div key={v} className={`bp-setup-pick ${dependants === v ? 'sel' : ''}`} onClick={() => update({ dependants: v })}>
              <div className="bp-setup-name">{n}</div>
              <div className="bp-setup-sub">{s}</div>
            </div>
          ))}
        </div>
      </div>

      {canContinue && (
        <button className="bp-btn bp-primary" onClick={() => update({ screen: 'occupation' })}>Continue →</button>
      )}
    </>
  );
};

// ============================================================
// OCCUPATION
// ============================================================
const OccupationScreen = ({ state, update }) => {
  const occs = [
    ['office', 'Office / Desk', 'Standard rates apply'],
    ['field', 'Field / Sales', 'Travel involved'],
    ['self_employed', 'Self-employed', 'Business owner, freelancer'],
    ['driver_rider', 'Driver / Boda rider', '⚠ Hazard loading applies'],
    ['hazardous', 'Construction / Mining', '⚠ Higher hazard loading'],
    ['medical', 'Medical / Healthcare', 'Standard or slight loading'],
  ];
  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'family' })}>← back</span>
      <h1 className="bp-display bp-title">Your <span className="bp-accent">work</span></h1>
      <p className="bp-tag bp-mono">This matters more than you think. Some occupations affect premiums and exclusions.</p>
      <div className="bp-card">
        <div className="bp-field-label">Closest match</div>
        <div className="bp-setup-grid">
          {occs.map(([v, n, s]) => (
            <div key={v} className={`bp-setup-pick ${state.occupation === v ? 'sel' : ''}`} onClick={() => update({ occupation: v })}>
              <div className="bp-setup-name">{n}</div>
              <div className="bp-setup-sub">{s}</div>
            </div>
          ))}
        </div>
      </div>
      {state.occupation && (
        <button className="bp-btn bp-primary" onClick={() => update({ screen: 'income' })}>Continue →</button>
      )}
    </>
  );
};

// ============================================================
// INCOME
// ============================================================
const IncomeScreen = ({ state, update }) => {
  const { income, maritalStatus, spouseIncome } = state;
  const canContinue = income && (maritalStatus === 'single' || spouseIncome !== null);
  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'occupation' })}>← back</span>
      <h1 className="bp-display bp-title">Your <span className="bp-accent">income</span></h1>
      <p className="bp-tag bp-mono">Monthly take-home. This determines what's affordable as a premium.</p>
      <div className="bp-card">
        <div className="bp-field-label">Your monthly net income</div>
        {INCOME_TIERS.map(t => (
          <button key={t.v} className={`bp-btn ${income === t.v ? 'sel' : ''}`} onClick={() => update({ income: t.v })}>
            <div className="bp-label">{t.label}</div>
            <div className="bp-meta">{t.desc}</div>
          </button>
        ))}
      </div>
      {maritalStatus === 'married' && (
        <div className="bp-card">
          <div className="bp-field-label">Your spouse's monthly income</div>
          <div className="bp-setup-grid">
            {[[0, "Not working", "You're sole earner"], [40000, 'Modest', '~KSh 40K/mo'], [80000, 'Comparable', '~KSh 80K/mo'], [150000, 'High earner', 'KSh 150K+/mo']].map(([v, n, s]) => (
              <div key={v} className={`bp-setup-pick ${spouseIncome === v ? 'sel' : ''}`} onClick={() => update({ spouseIncome: v })}>
                <div className="bp-setup-name">{n}</div>
                <div className="bp-setup-sub">{s}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {canContinue && (
        <button className="bp-btn bp-primary" onClick={() => update({ screen: 'health' })}>Continue →</button>
      )}
    </>
  );
};

// ============================================================
// HEALTH
// ============================================================
const HealthScreen = ({ state, update }) => {
  const { health } = state;
  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'income' })}>← back</span>
      <h1 className="bp-display bp-title">Your <span className="bp-accent">health</span></h1>
      <p className="bp-tag bp-mono">Be honest with yourself here. The game will test what you do with this information later.</p>
      <div className="bp-card">
        <div className="bp-field-label">Closest description</div>
        {[
          ['clean', 'Clean bill of health', 'No diagnoses, no chronic conditions, no surprises in your records'],
          ['minor', 'Minor pre-existing condition', 'e.g., mild hypertension, controlled with one medication; or seasonal asthma'],
          ['chronic', 'Ongoing chronic condition', 'e.g., diabetes, heart condition, requires regular medical monitoring'],
        ].map(([v, n, s]) => (
          <button key={v} className={`bp-btn ${health === v ? 'sel' : ''}`} onClick={() => update({ health: v })}>
            <div className="bp-label">{n}</div>
            <div className="bp-meta">{s}</div>
          </button>
        ))}
      </div>
      {health && (
        <>
          <div className="bp-card bp-alert">
            <p><strong>Quick note before continuing:</strong> what you declared here is between you and this game. In real life, what you declare on an insurance application is between you and the insurer's underwriters — and the medical examiner. We'll come back to this.</p>
          </div>
          <button className="bp-btn bp-primary" onClick={() => update({ screen: 'product_intro' })}>Continue to products →</button>
        </>
      )}
    </>
  );
};

// ============================================================
// PRODUCT INTRO
// ============================================================
const ProductIntroScreen = ({ state, update }) => {
  const { age } = state;
  const [openMath, setOpenMath] = useState(null);
  const products = [
    {
      key: 'term',
      name: 'Term Life',
      badge: 'Cheapest',
      desc: "Pure protection for a fixed period (10, 20, or 30 years). If you die during the term, full payout to your family. If you survive the term, no payout — the premiums were the cost of protection over those years. Like car insurance: you don't expect a refund if you don't crash.",
      math: `At age ${age}, 5M cover, 20 years:\nPremium: KSh ${fmt(premiumPer1M(age, 'term') * 5)}/mo\nCost of protection over 20 years: KSh ${fmt(premiumPer1M(age, 'term') * 5 * 12 * 20)} total in premiums\nIf you die in the term: 5,000,000 paid to your family\nIf you survive: no payout — you got 20 years of protection (the product worked as designed)\nFor comparison: a Whole Life policy with the same 5M cover would cost KSh ${fmt(premiumPer1M(age, 'whole') * 5 * 12 * 20)} over the same 20 years`,
    },
    {
      key: 'whole',
      name: 'Whole Life',
      badge: 'Mid-cost',
      desc: 'Covers you for life. Higher premium, but it builds a "cash value" you can borrow against or surrender. Pays out whenever you die — could be at 80, could be at 35. Often missold as "savings with protection."',
      math: `At age ${age}, 5M cover:\nPremium: KSh ${fmt(premiumPer1M(age, 'whole') * 5)}/mo\nTotal premiums over 25 years: KSh ${fmt(premiumPer1M(age, 'whole') * 5 * 12 * 25)}\nCash surrender value at 25 years: ~60–75% of premiums paid\nIf you die anytime: 5,000,000 payout`,
    },
    {
      key: 'endowment',
      name: 'Endowment',
      badge: 'Most expensive',
      desc: 'Hybrid of savings + protection. Pays out at maturity if you survive, or on death if earlier. Most expensive monthly cost. Popular product in Kenya, but the maturity benefit often lags simple investments.',
      math: `At age ${age}, 5M cover, 20 years:\nPremium: KSh ${fmt(premiumPer1M(age, 'endow') * 5)}/mo\nTotal premiums: KSh ${fmt(premiumPer1M(age, 'endow') * 5 * 12 * 20)}\nMaturity benefit at end of 20 years: ~5,000,000 + bonuses (often ~6–7M)`,
    },
  ];
  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'health' })}>← back</span>
      <h1 className="bp-display bp-title">The three <span className="bp-accent">products</span></h1>
      <p className="bp-tag bp-mono">Same coverage amount, completely different products. Tap each to understand.</p>
      {products.map(p => (
        <div key={p.key} className="bp-product-card">
          <div className="bp-product-head">
            <div className="bp-product-name">{p.name}</div>
            <div className="bp-product-premium">{p.badge}</div>
          </div>
          <div className="bp-product-desc">{p.desc}</div>
          <div className="bp-show-math" onClick={() => setOpenMath(openMath === p.key ? null : p.key)}>
            {openMath === p.key ? '▾ Hide math' : '▸ Show me the math'}
          </div>
          {openMath === p.key && (
            <div className="bp-math">{p.math.split('\n').map((l, i) => <div key={i}>{l}</div>)}</div>
          )}
        </div>
      ))}
      <div className="bp-card bp-alert">
        <p><strong>The fourth option people forget:</strong> Buy term, invest the difference. Take the cheapest cover (term), put the savings you didn't spend on whole life or endowment into a Money Market Fund or SACCO. We'll show this path side-by-side.</p>
      </div>
      <button className="bp-btn bp-primary" onClick={() => update({ screen: 'product_choice' })}>Make your choice →</button>
    </>
  );
};

// ============================================================
// PRODUCT CHOICE
// ============================================================
const ProductChoiceScreen = ({ state, update }) => {
  const sa = 5000000;
  const base = { ...state, riderCritical: false, riderDisability: false };
  const termP = calculatePremium(base, 'term', sa);
  const wholeP = calculatePremium(base, 'whole', sa);
  const endowP = calculatePremium(base, 'endow', sa);
  const diff = wholeP - termP;
  const { product, riderCritical, riderDisability } = state;

  return (
    <>
      <span className="bp-back-link" onClick={() => update({ screen: 'product_intro' })}>← back</span>
      <h1 className="bp-display bp-title">Your <span className="bp-accent">choice</span></h1>
      <p className="bp-tag bp-mono">5M sum assured. Choose your product. Riders are next.</p>

      {[
        ['term', `Term Life — 20 years, 5M cover`, 'Cheapest. Pure protection. No payout if you survive the term.', termP, `KSh ${fmt(termP)}/mo`],
        ['whole', `Whole Life — Lifetime, 5M cover`, `Pays out whenever you die. Builds cash value. ${Math.round(wholeP / termP)}× the term cost.`, wholeP, `KSh ${fmt(wholeP)}/mo`],
        ['endowment', `Endowment — 20 years, 5M cover`, `Pays at maturity or death. ${Math.round(endowP / termP)}× the term cost. Savings + cover hybrid.`, endowP, `KSh ${fmt(endowP)}/mo`],
        ['term_invest', `Term + Invest the Difference`, 'Take cheap term cover, put the savings (vs whole life) into MMF at ~10% pa. Tracked separately.', termP, `KSh ${fmt(termP)}/mo + ${fmt(diff)} to MMF`],
      ].map(([v, label, meta, , cost]) => (
        <button key={v} className={`bp-btn ${product === v ? 'sel' : ''}`}
          onClick={() => update({ product: v, sumAssured: sa })}>
          <span className="bp-cost">{cost}</span>
          <div className="bp-label">{label}</div>
          <div className="bp-meta">{meta}</div>
        </button>
      ))}

      {product && (
        <div className="bp-card">
          <div className="bp-field-label">Add riders? (Optional)</div>
          <button className={`bp-btn ${riderCritical ? 'sel' : ''}`} onClick={() => update({ riderCritical: !riderCritical })}>
            <span className="bp-cost">+KSh {fmt(380 * 5)}/mo</span>
            <div className="bp-label">Critical Illness Rider {riderCritical ? '✓' : ''}</div>
            <div className="bp-meta">Lump sum payout on diagnosis of covered conditions (cancer, heart attack, stroke, kidney failure, etc).</div>
          </button>
          <button className={`bp-btn ${riderDisability ? 'sel' : ''}`} onClick={() => update({ riderDisability: !riderDisability })}>
            <span className="bp-cost">+KSh {fmt(220 * 5)}/mo</span>
            <div className="bp-label">Disability Rider {riderDisability ? '✓' : ''}</div>
            <div className="bp-meta">Premium waived + lump sum if you become permanently disabled.</div>
          </button>
        </div>
      )}

      {product && (
        <button className="bp-btn bp-primary" onClick={() => update({ screen: 'agent' })}>Meet the agent →</button>
      )}
    </>
  );
};

// ============================================================
// AGENT
// ============================================================
const AgentScreen = ({ state, update }) => {
  const { product } = state;
  let pitch = '';
  const agentName = 'Agent Macharia';
  if (product === 'term' || product === 'term_invest') {
    pitch = `"Boss, I see you've picked term life. Sasa, let me be honest with you — that's a young man's product. After 20 years, that money is gone. <strong>Gone</strong>. Now look, if you go whole life, every shilling is also building you a cash value. Think of it as forced savings PLUS protection. By the time you're 50, that policy will have <em>at least</em> KSh 8 million in surrender value. That's a small house. Why would you waste your premiums on term when you can have both?"`;
  } else if (product === 'whole') {
    pitch = `"Good choice on whole life. Now hear me out — we have a special endowment product, it's actually an investment plan with insurance attached. The projections show 12% annual returns, way better than your SACCO. Plus full cover. I can swap you over today and waive the new application fees. Trust me, this is the one our wealthy clients all take."`;
  } else {
    pitch = `"Endowment is a good pick. But let me show you our 'premium' endowment — it pays double bonuses at maturity. And here's the thing — if you take a 10-year policy instead of 20, the maturity is sooner and the bonuses are higher per year. I can write you up right now, just sign here and here."`;
  }

  const handleChoice = (choice) => {
    let story = '';
    let tone = '';
    let newProduct = product;

    if (choice === 'pushed_back') {
      story = "The agent stammers. He doesn't actually have the comparison numbers — he was working off the marketing brochure. He calls his manager, comes back, and the figures look very different. The 'assumed 8 million surrender value' was the projected maturity benefit on a different product entirely. He apologizes, you keep your original choice. <strong>You just dodged a misselling attempt by asking one specific question.</strong>";
      tone = 'good';
    } else if (choice === 'asked_questions') {
      story = "He laughs nervously. \"Commission ni kawaida tu, boss.\" Eventually he admits: whole life pays him roughly 50% of the first year's premium as commission, plus trail commissions. Term life pays him about 15% of first year only. <strong>Now you understand why he pushed whole life so hard. It wasn't about your protection. It was about his income.</strong> You stick with your original choice.";
      tone = 'good';
    } else if (choice === 'thought_more') {
      story = 'You leave the meeting and check independent sources online (Money254, AKI website, IRA published statistics on claims and surrender values). The numbers tell a different story than the agent\'s brochure. Whole life cash values at 20 years are typically 60-75% of premiums paid — not 200%. <strong>You return the following week and stick with your original choice, with eyes open.</strong>';
      tone = 'good';
    } else {
      story = "You sign. The product you walked out with is not the one you came in wanting. The agent's commission is higher. Your monthly premium is higher. The fit with your actual life situation is worse. <strong>This is the textbook outcome of insurance misselling in Kenya. It happens thousands of times every month.</strong>";
      tone = 'bad';
      if (product === 'term' || product === 'term_invest') newProduct = 'whole';
      else if (product === 'whole') newProduct = 'endowment';
    }

    const nextScreen = state.tourMode === 'quick' ? 'quick_application' : 'application';
    update({
      agentChoice: choice,
      agentSeen: true,
      product: newProduct,
      pendingStory: { story, tone, nextScreen },
      screen: 'story_then',
    });
  };

  return (
    <>
      <h1 className="bp-display bp-title">The <span className="bp-accent">agent</span> appears</h1>
      <p className="bp-tag bp-mono">Most insurance in Kenya is sold by agents. Some are professionals. Some are not.</p>
      <div className="bp-card">
        <span className="bp-pill bp-agent">INSURANCE AGENT</span>
        <h3 style={{ marginTop: 12 }}>You're meeting {agentName} to "finalize" your policy</h3>
        <p>{agentName} sits down with you over chai. After 10 minutes of small talk about your family, he leans forward.</p>
        <div className="bp-agent-bubble">
          <span className="bp-agent-who">{agentName}</span>
          <span dangerouslySetInnerHTML={{ __html: pitch }} />
        </div>
        <p><strong>How do you respond?</strong></p>
      </div>
      <button className="bp-btn bp-good" onClick={() => handleChoice('pushed_back')}>
        <div className="bp-label">Push back — ask hard questions</div>
        <div className="bp-meta">"What's the actual cash value at 20 years vs total premiums paid? Show me the numbers."</div>
      </button>
      <button className="bp-btn bp-warn" onClick={() => handleChoice('asked_questions')}>
        <div className="bp-label">Listen, but ask what his commission is on each product</div>
        <div className="bp-meta">"Out of curiosity, what's your commission on whole life vs term?"</div>
      </button>
      <button className="bp-btn" onClick={() => handleChoice('thought_more')}>
        <div className="bp-label">Say you'll think about it and check independent reviews</div>
        <div className="bp-meta">"Let me research and come back to you next week."</div>
      </button>
      <button className="bp-btn bp-danger" onClick={() => handleChoice('agreed')}>
        <div className="bp-label">Let him talk you into changing — sign what he recommends</div>
        <div className="bp-meta">"You're the expert. Where do I sign?"</div>
      </button>
    </>
  );
};

// ============================================================
// STORY THEN (intermediary)
// ============================================================
const StoryThenScreen = ({ state, update }) => {
  const { pendingStory } = state;
  if (!pendingStory) return null;
  const { story, tone, nextScreen } = pendingStory;

  const handleContinue = () => {
    if (nextScreen === 'advance_year') {
      const nextIdx = state.yearIdx + 1;
      if (nextIdx >= LIFE_EVENT_OFFSETS.length) {
        if (state.claimOutcome && state.claimOutcome.startsWith('denied') && !state.iraComplaintFiled && state.surrenderedAmount === 0) {
          update({ screen: 'ira_choice', yearIdx: nextIdx, pendingStory: null });
        } else {
          update({ screen: 'end', yearIdx: nextIdx, pendingStory: null });
        }
      } else {
        update({ screen: 'life_events', yearIdx: nextIdx, pendingStory: null });
      }
    } else if (nextScreen === 'lapse_decision') {
      update({ screen: 'lapse_decision', pendingStory: null });
    } else {
      update({ screen: nextScreen, pendingStory: null });
    }
  };

  return (
    <div className="bp-card">
      <h3>What happened</h3>
      <div className={`bp-story ${tone}`} dangerouslySetInnerHTML={{ __html: story }} />
      <button className="bp-btn bp-primary" onClick={handleContinue}>Continue →</button>
    </div>
  );
};

// ============================================================
// QUICK APPLICATION
// ============================================================
const QuickApplicationScreen = ({ state, update }) => {
  const { discCondition, readExclusions } = state;

  const handleComplete = () => {
    const premium = calculatePremium(state, state.product === 'term_invest' ? 'term' : state.product, state.sumAssured);
    update({
      discSource: 'salary',
      amlIDProvided: 'id_payslip',
      amlOutcome: 'approved',
      monthlyPremium: premium,
      screen: 'confirmation',
    });
  };

  return (
    <>
      <h1 className="bp-display bp-title">The <span className="bp-accent">application</span></h1>
      <p className="bp-tag bp-mono">Two questions. Both matter more than they look.</p>

      <div className="bp-card">
        <h3>1. Be honest about your health and lifestyle?</h3>
        <p style={{ fontSize: 14 }}>The insurance application asks about smoking, pre-existing conditions, hazardous activities (like riding a motorcycle), and your job. Honest answers might increase your premium 15-40%. Dishonest answers might void any future claim entirely.</p>
        <button className={`bp-btn ${discCondition === 'honest' ? 'sel' : ''}`} onClick={() => update({ discCondition: 'honest', discHazard: 'honest', discSmoking: 'no' })}>
          <div className="bp-label">Yes, tell the truth on everything</div>
          <div className="bp-meta">Premium may load if you have conditions/risks. Claims will stand.</div>
        </button>
        <button className={`bp-btn ${discCondition === 'hide' ? 'sel' : ''}`} onClick={() => update({ discCondition: 'hide', discHazard: 'hide', discSmoking: 'hide' })}>
          <div className="bp-label">No, hide things that might raise the premium</div>
          <div className="bp-meta">Lower premium now. Claims at risk of denial later.</div>
        </button>
      </div>

      {discCondition && (
        <div className="bp-card">
          <h3>2. Read the policy document?</h3>
          <p style={{ fontSize: 14 }}>The agent hands you a 47-page document with the policy exclusions — what is NOT covered. Reading takes ~5 minutes.</p>
          <button className={`bp-btn ${readExclusions === true ? 'sel' : ''}`} onClick={() => update({ readExclusions: true })}>
            <div className="bp-label">Yes, read it carefully</div>
            <div className="bp-meta">You'll know what would NOT be paid.</div>
          </button>
          <button className={`bp-btn ${readExclusions === false ? 'sel' : ''}`} onClick={() => update({ readExclusions: false })}>
            <div className="bp-label">No, just sign and go</div>
            <div className="bp-meta">Like 90% of people do. You'll find out at claim time.</div>
          </button>
        </div>
      )}

      {readExclusions === true && (
        <div className="bp-card bp-ira">
          <h3>📜 Key exclusions in your policy</h3>
          <ul style={{ margin: '8px 0 8px 18px', fontSize: 13, color: 'var(--ink-mid)', lineHeight: 1.8 }}>
            <li><strong>Suicide:</strong> Death by suicide in first 24 months — no payout.</li>
            <li><strong>Lapse:</strong> Miss 3+ premiums → policy ends.</li>
            <li><strong>Non-disclosure:</strong> Hidden info found at claim time → policy voided.</li>
            <li><strong>Drug/alcohol-related death:</strong> Excluded.</li>
            <li><strong>War/terrorism deaths:</strong> Excluded.</li>
            <li><strong>Critical illness rider:</strong> 6-month waiting period before claims.</li>
          </ul>
        </div>
      )}

      {discCondition && readExclusions !== null && (
        <button className="bp-btn bp-primary" onClick={handleComplete}>Issue the policy →</button>
      )}
    </>
  );
};

// ============================================================
// FULL APPLICATION (disclosure screen)
// ============================================================
const ApplicationScreen = ({ state, update }) => {
  const { health, occupation, discSmoking, discCondition, discHazard, readExclusions } = state;
  const needsCondition = health === 'minor' || health === 'chronic';
  const needsHazard = occupation === 'driver_rider' || occupation === 'hazardous' || occupation === 'field';
  const canContinue = discSmoking !== null && (discCondition !== null || !needsCondition) && (discHazard !== null || !needsHazard) && readExclusions !== null;

  const DiscRow = ({ question, field, options }) => (
    <div className="bp-disclosure-row">
      <div className="bp-disc-q">{question}</div>
      <div className="bp-disc-opts">
        {options.map(([val, label, cls]) => (
          <button key={val} className={`${state[field] === val ? `sel ${cls}` : ''}`}
            onClick={() => update({ [field]: val })}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <h1 className="bp-display bp-title">The <span className="bp-accent">application</span></h1>
      <p className="bp-tag bp-mono">A real insurance application has 80+ questions. These are the five that matter most for claims.</p>

      <div className="bp-card">
        <h3>Section A: Lifestyle &amp; Health</h3>
        <p style={{ fontSize: 13 }}>Be honest or hide? Each choice changes what happens at claim time.</p>
        <DiscRow question="1. Do you smoke cigarettes, shisha, or vape?" field="discSmoking"
          options={[['honest', 'Yes, declare', 'honest'], ['no', 'No, never', 'neutral'], ['hide', 'Hide it', 'hide']]} />
        {needsCondition ? (
          <DiscRow question={`2. Any pre-existing conditions? (You have a ${health === 'chronic' ? 'chronic' : 'minor'} condition)`}
            field="discCondition"
            options={[['honest', 'Declare honestly', 'honest'], ['hide', 'Hide it', 'hide']]} />
        ) : (
          <div className="bp-disclosure-row">
            <div className="bp-disc-q">2. Any pre-existing conditions?</div>
            <div className="bp-disc-opts"><button className="sel neutral">No — clean bill</button></div>
          </div>
        )}
        {needsHazard ? (
          <DiscRow question="3. Do you ride a motorcycle, work in construction, or travel to high-risk areas?"
            field="discHazard"
            options={[['honest', 'Yes, declare', 'honest'], ['hide', 'Hide it', 'hide']]} />
        ) : (
          <div className="bp-disclosure-row">
            <div className="bp-disc-q">3. Do you engage in hazardous activities?</div>
            <div className="bp-disc-opts"><button className="sel neutral">No — office work</button></div>
          </div>
        )}
      </div>

      <div className="bp-card">
        <h3>Section B: The exclusions document</h3>
        <p style={{ fontSize: 13 }}>The agent slides a 47-page policy document across the table and points to a signature line.</p>
        <div className="bp-disclosure-row">
          <div className="bp-disc-q">4. Read the policy exclusions before signing?</div>
          <div className="bp-disc-opts">
            <button className={readExclusions === true ? 'sel honest' : ''} onClick={() => update({ readExclusions: true })}>Read carefully (~5 min)</button>
            <button className={readExclusions === false ? 'sel hide' : ''} onClick={() => update({ readExclusions: false })}>Skip — sign now</button>
          </div>
        </div>
      </div>

      {readExclusions === true && (
        <div className="bp-card bp-ira">
          <h3>📜 Key exclusions in your policy</h3>
          <ul style={{ margin: '8px 0 8px 18px', fontSize: 13, lineHeight: 1.8 }}>
            <li><strong>24-month suicide clause:</strong> Death by suicide within first 24 months — no payout.</li>
            <li><strong>Non-disclosure void:</strong> Hidden material information → policy voided at claim.</li>
            <li><strong>Lapse:</strong> Miss 3+ consecutive premiums → policy lapses.</li>
            <li><strong>Drug/alcohol exclusion:</strong> Death while intoxicated excluded.</li>
            <li><strong>War &amp; terrorism exclusion.</strong></li>
            <li><strong>Critical Illness rider waiting period:</strong> 6 months from policy start.</li>
          </ul>
        </div>
      )}
      {readExclusions === false && (
        <div className="bp-card bp-alert">
          <p>"Sawa boss, sign hapa. Tutakuletea hard copy baadaye." The agent files the form. You'll see what was in the exclusions later — possibly when you try to claim.</p>
        </div>
      )}

      {canContinue && (
        <button className="bp-btn bp-primary" onClick={() => update({ screen: 'aml' })}>Continue to verification →</button>
      )}
    </>
  );
};

// ============================================================
// AML
// ============================================================
const AMLScreen = ({ state, update }) => {
  const { discSource, amlIDProvided } = state;

  const handleComplete = () => {
    const flagged = discSource === 'evasive' || amlIDProvided === 'refused';
    const premium = calculatePremium(state, state.product === 'term_invest' ? 'term' : state.product, state.sumAssured);
    if (flagged) {
      update({ amlFlagged: true, monthlyPremium: premium, screen: 'aml_consequence' });
    } else {
      update({ amlFlagged: false, amlOutcome: 'approved', monthlyPremium: premium, screen: 'confirmation' });
    }
  };

  return (
    <>
      <h1 className="bp-display bp-title">Identity <span className="bp-accent">verification</span></h1>
      <p className="bp-tag bp-mono">The insurer needs to know who you are and where the money is coming from.</p>

      <div className="bp-card bp-ira">
        <h3>Why these questions?</h3>
        <p>Under Kenya's <strong>Proceeds of Crime and Anti-Money Laundering Act (POCAMLA)</strong>, insurers are required to verify your identity, understand your source of funds, and report suspicious activity.</p>
        <p>This is not bureaucratic harassment. Without it, criminals would route stolen or laundered money through insurance policies. Every regulated insurer in the world does this.</p>
      </div>

      <div className="bp-card">
        <h3>Section C: KYC and source of funds</h3>
        <div className="bp-disclosure-row">
          <div className="bp-disc-q">5. What documents can you provide?</div>
          <div className="bp-disc-opts">
            <button className={amlIDProvided === 'id_payslip' ? 'sel honest' : ''} onClick={() => update({ amlIDProvided: 'id_payslip' })}>ID + payslip + KRA PIN</button>
            <button className={amlIDProvided === 'id_only' ? 'sel neutral' : ''} onClick={() => update({ amlIDProvided: 'id_only' })}>ID only</button>
            <button className={amlIDProvided === 'refused' ? 'sel hide' : ''} onClick={() => update({ amlIDProvided: 'refused' })}>"Why do you need all this?"</button>
          </div>
        </div>
        <div className="bp-disclosure-row">
          <div className="bp-disc-q">6. Source of the funds you'll use to pay premiums?</div>
          <div className="bp-disc-opts">
            <button className={discSource === 'salary' ? 'sel honest' : ''} onClick={() => update({ discSource: 'salary' })}>Monthly salary</button>
            <button className={discSource === 'business' ? 'sel honest' : ''} onClick={() => update({ discSource: 'business' })}>Business income</button>
            <button className={discSource === 'family' ? 'sel neutral' : ''} onClick={() => update({ discSource: 'family' })}>Family will pay</button>
            <button className={discSource === 'evasive' ? 'sel hide' : ''} onClick={() => update({ discSource: 'evasive' })}>"It's mine, that's enough"</button>
          </div>
        </div>
      </div>

      {discSource === 'family' && (
        <div className="bp-card bp-alert">
          <p><span className="bp-pill bp-aml">⚠ AML FLAG</span></p>
          <p style={{ marginTop: 8 }}><strong>Third-party premium payments are a red flag.</strong> If someone else is paying for your insurance, the insurer must understand why and verify that person's identity and source of funds too. This is one of the most common patterns in money laundering through insurance. This triggers Enhanced Due Diligence.</p>
        </div>
      )}
      {(discSource === 'evasive' || amlIDProvided === 'refused') && (
        <div className="bp-card bp-danger">
          <p><span className="bp-pill bp-aml">⚠ APPLICATION FLAGGED</span></p>
          <p style={{ marginTop: 8 }}>The compliance officer reviews your application. Refusing to disclose ID or source of funds triggers a Suspicious Transaction Report (STR) to the <strong>Financial Reporting Centre</strong>. Your application is referred to enhanced screening.</p>
        </div>
      )}

      {amlIDProvided && discSource && (
        <button className="bp-btn bp-primary" onClick={handleComplete}>Complete application →</button>
      )}
    </>
  );
};

// ============================================================
// AML CONSEQUENCE
// ============================================================
const AMLConsequenceScreen = ({ state, update }) => {
  const { amlIDProvided, discSource } = state;
  let outcome;
  if (amlIDProvided === 'refused' && discSource === 'evasive') outcome = 'rejected';
  else if (amlIDProvided === 'refused' || discSource === 'evasive') outcome = 'delayed';
  else outcome = 'conditional';

  return (
    <div className="bp-card bp-ira">
      <span className="bp-pill bp-aml">⚠ AML / KYC OUTCOME</span>
      <h2 style={{ marginTop: 12 }}>The compliance review</h2>
      {outcome === 'rejected' && (
        <>
          <p><strong>Application rejected.</strong></p>
          <div className="bp-story bad">
            The compliance officer files a Suspicious Transaction Report with the <strong>Financial Reporting Centre</strong>. Your application is declined. The insurer's letter cites "inability to complete customer due diligence required under POCAMLA." Your details may be shared with other insurers — getting cover anywhere becomes harder.
          </div>
          <p>The game continues with no cover in force. You'll feel the absence at every life event from here.</p>
          <button className="bp-btn bp-primary" onClick={() => update({ amlOutcome: 'rejected', policyLapsed: true, lapsedAtYear: 0, screen: 'confirmation' })}>Continue without cover →</button>
        </>
      )}
      {outcome === 'delayed' && (
        <>
          <p><strong>Application delayed pending Enhanced Due Diligence.</strong></p>
          <div className="bp-story alert">
            The compliance team escalates. They request: certified ID copy, payslips for the last 6 months, KRA tax compliance certificate, bank statements showing the source of your premium funds, and an explanation letter. Six weeks pass. Eventually they accept, but with conditions: the policy is approved at a 12% premium loading.
          </div>
          <button className="bp-btn bp-primary" onClick={() => update({ amlOutcome: 'delayed', monthlyPremium: Math.round(state.monthlyPremium * 1.12), screen: 'confirmation' })}>Accept the conditions, continue with policy →</button>
          <button className="bp-btn bp-danger" onClick={() => update({ amlOutcome: 'rejected', policyLapsed: true, lapsedAtYear: 0, screen: 'confirmation' })}>Walk away — no policy</button>
        </>
      )}
      {outcome === 'conditional' && (
        <>
          <p><strong>Conditional approval.</strong></p>
          <div className="bp-story">
            Your application is accepted but flagged for enhanced monitoring. No premium loading. The flag mostly affects future high-value transactions — top-ups, requests for early surrender, beneficiary changes — all will trigger additional KYC.
          </div>
          <button className="bp-btn bp-primary" onClick={() => update({ amlOutcome: 'conditional', screen: 'confirmation' })}>Continue with policy →</button>
        </>
      )}
    </div>
  );
};

// ============================================================
// CONFIRMATION
// ============================================================
const ConfirmationScreen = ({ state, update }) => {
  const { sumAssured, product, riderCritical, riderDisability, monthlyPremium, age } = state;
  const sa = sumAssured || 5000000;
  const wholeP = calculatePremium({ ...state, riderCritical: false, riderDisability: false }, 'whole', sa);
  const termP = calculatePremium({ ...state, riderCritical: false, riderDisability: false }, 'term', sa);
  const monthlyToInvest = product === 'term_invest' ? (wholeP - termP) : 0;
  const productLabel = product === 'term' ? 'Term Life — 20 years' : product === 'whole' ? 'Whole Life' : product === 'endowment' ? 'Endowment — 20 years' : 'Term Life + Side Investment';

  return (
    <>
      <h1 className="bp-display bp-title">Policy <span className="bp-accent">issued</span></h1>
      <p className="bp-tag bp-mono">Your policy is now active. The clock starts now.</p>
      <div className="bp-card bp-gold">
        <h3>Your policy summary</h3>
        <p><strong>Product:</strong> {productLabel}</p>
        <p><strong>Sum assured:</strong> KSh {fmt(sa)}</p>
        <p><strong>Riders:</strong> {[riderCritical && 'Critical Illness', riderDisability && 'Disability'].filter(Boolean).join(', ') || 'None'}</p>
        <p><strong>Monthly premium:</strong> KSh {fmt(monthlyPremium)}</p>
        {monthlyToInvest > 0 && <p><strong>Plus monthly to MMF:</strong> KSh {fmt(monthlyToInvest)} (auto-debit at 10% pa)</p>}
        <p><strong>Your age now:</strong> {age}</p>
      </div>
      <div className="bp-card">
        <h3>Now we time-travel</h3>
        <p>Twenty-five years are about to pass. Five life events will hit you — at year 3, year 8, year 13, year 18, and year 23. Some are small, some are claim-events. The choices you've already made will play out.</p>
      </div>
      <button className="bp-btn bp-primary" onClick={() => update({ yearIdx: 0, screen: 'life_events' })}>Begin year-by-year →</button>
    </>
  );
};

// ============================================================
// HUD
// ============================================================
const Hud = ({ state }) => {
  const currentAge = state.age + (LIFE_EVENT_OFFSETS[state.yearIdx] || 0);
  return (
    <div className="bp-hud bp-hud-5">
      {[
        [currentAge, 'Age'],
        [fmt(state.monthlyPremium), 'Monthly Premium'],
        [fmt(state.totalPremiumsPaid), 'Premiums Paid'],
        [state.policyLapsed ? 'Lapsed' : 'In Force', 'Policy', state.policyLapsed ? 'bp-neg' : 'bp-pos'],
        [fmt(state.sumAssured), 'Sum Assured'],
      ].map(([v, l, cls = ''], i) => (
        <div key={i} className="bp-stat">
          <div className={`bp-stat-v ${cls}`}>{v}</div>
          <div className="bp-stat-l">{l}</div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// LIFE EVENTS
// ============================================================
const LifeEventScreen = ({ state, update, applyChoice }) => {
  const { yearIdx, policyLapsed, monthlyPremium, sumAssured } = state;

  // Safety: ensure premium is set
  if (!monthlyPremium && state.product && sumAssured && !policyLapsed) {
    update({ monthlyPremium: calculatePremium(state, state.product === 'term_invest' ? 'term' : state.product, sumAssured) });
    return null;
  }

  const yearOffset = LIFE_EVENT_OFFSETS[yearIdx];
  const event = getActiveEvent(yearOffset, state);
  const choices = event.choices(state).filter(Boolean);

  // Stash claim variant
  if (event.claimType && state.claimEventVariant !== event.claimType) {
    update({ claimEventVariant: event.claimType });
  }

  const handleChoice = (i) => {
    const choice = choices[i];
    applyChoice((draft) => {
      // Carry claimEventVariant into draft
      if (event.claimType) draft.claimEventVariant = event.claimType;
      return choice.apply(draft);
    });
    update({ screen: 'story_then' });
  };

  return (
    <>
      <Hud state={state} />
      <div className="bp-progress">
        {LIFE_EVENT_OFFSETS.map((_, i) => (
          <div key={i} className={`bp-seg ${i < yearIdx ? 'done' : i === yearIdx ? 'cur' : ''}`} />
        ))}
      </div>
      <div className="bp-year-banner">
        YEAR {yearOffset} OF 25 — YOU ARE NOW {state.age + yearOffset} YEARS OLD
        {policyLapsed ? ' • POLICY LAPSED' : ''}
      </div>
      <div className="bp-card">
        <span className="bp-pill bp-q">EVENT {yearIdx + 1} OF {LIFE_EVENT_OFFSETS.length}</span>
        <h2 style={{ marginTop: 12 }}>{event.title}</h2>
        <p dangerouslySetInnerHTML={{ __html: event.setup(state) }} />
        {policyLapsed && (
          <div className="bp-red-flag">
            <strong>Your policy is lapsed.</strong> You have no cover in force. Some options below may be unavailable.
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          {choices.map((c, i) => (
            <button key={i} className="bp-btn" onClick={() => handleChoice(i)}>
              <div className="bp-label">{c.label}</div>
              <div className="bp-meta">{c.meta || ''}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// ============================================================
// LAPSE DECISION
// ============================================================
const LapseDecisionScreen = ({ state, update, applyChoice }) => {
  const currentAge = state.age + (LIFE_EVENT_OFFSETS[state.yearIdx] || state.lapsedAtYear);
  const sa = 5000000;
  const newTermP = premiumPer1M(currentAge, 'term') * 5;
  const newWholeP = premiumPer1M(currentAge, 'whole') * 5;
  const oldTermP = premiumPer1M(state.age, 'term') * 5;
  const oldWholeP = premiumPer1M(state.age, 'whole') * 5;
  const termIncrease = Math.round((newTermP / oldTermP - 1) * 100);
  const wholeIncrease = Math.round((newWholeP / oldWholeP - 1) * 100);

  const handleBuyNew = (type) => {
    const newPremium = calculatePremium({ ...state, age: currentAge }, type, sa);
    const story = `You apply again at age ${currentAge}. Medical exam goes through. New policy is issued at KSh ${fmt(newPremium)}/month — significantly higher than what you were paying before. The premiums from your previous policy are gone. You're starting over. <strong>This is the actual cost of letting a policy lapse: the difference between original and replacement premium, compounded over the remaining years.</strong>`;
    update({
      boughtNewPolicy: true,
      newPolicyAge: currentAge,
      newPolicySumAssured: sa,
      newPolicyPremium: newPremium,
      product: type,
      monthlyPremium: newPremium,
      sumAssured: sa,
      policyLapsed: false,
      lapsedAtYear: null,
      riderCritical: false,
      riderDisability: false,
      pendingStory: { story, tone: 'alert', nextScreen: 'advance_year' },
      screen: 'story_then',
    });
  };

  const handleStayUninsured = () => {
    const story = `You decide to self-insure. No premium going out, but no protection in. ${state.children > 0 || (state.maritalStatus === 'married' && state.spouseIncome === 0) ? 'Your dependants are now relying on your continued health.' : "You're on your own."} If something happens, savings absorb it. If savings aren't enough, family or debt picks up the rest. This works for some people. It catastrophically fails for others.`;
    update({
      pendingStory: { story, tone: 'neutral', nextScreen: 'advance_year' },
      screen: 'story_then',
    });
  };

  return (
    <>
      <div className="bp-card bp-alert">
        <span className="bp-pill bp-danger">⚠ POLICY LAPSED</span>
        <h2 style={{ marginTop: 12 }}>You have no cover</h2>
        <p>Your policy is no longer in force. You've already paid <strong>KSh {fmt(state.totalPremiumsPaid)}</strong> in premiums.{state.surrenderedAmount > 0 && ` You recovered <strong>KSh ${fmt(state.surrenderedAmount)}</strong> via surrender.`} The rest is sunk.</p>
        <p><strong>If you want cover going forward, you must apply again — at your current age ({currentAge}), not the age you originally signed up at ({state.age}).</strong></p>
        <div className="bp-compare-row bp-head"><div>Product</div><div>Then (age {state.age})</div><div>Now (age {currentAge})</div><div>Δ</div></div>
        <div className="bp-compare-row"><div>Term life</div><div>{fmt(oldTermP)}/mo</div><div>{fmt(newTermP)}/mo</div><div className="bp-v bp-neg">+{termIncrease}%</div></div>
        <div className="bp-compare-row"><div>Whole life</div><div>{fmt(oldWholeP)}/mo</div><div>{fmt(newWholeP)}/mo</div><div className="bp-v bp-neg">+{wholeIncrease}%</div></div>
        <p style={{ fontSize: 13, marginTop: 12 }}><em>Plus: any health conditions developed in the intervening years will trigger underwriting loading or exclusions on the new policy.</em></p>
      </div>

      <div className="bp-card">
        <h3>What do you want to do?</h3>
        <button className="bp-btn" onClick={() => handleBuyNew('term')}>
          <span className="bp-cost">KSh {fmt(newTermP)}/mo</span>
          <div className="bp-label">Buy new Term Life (20 years, 5M cover)</div>
          <div className="bp-meta">Cheapest option. Pure protection.</div>
        </button>
        <button className="bp-btn" onClick={() => handleBuyNew('whole')}>
          <span className="bp-cost">KSh {fmt(newWholeP)}/mo</span>
          <div className="bp-label">Buy new Whole Life (lifetime, 5M cover)</div>
          <div className="bp-meta">Builds cash value. More expensive especially at older ages.</div>
        </button>
        <button className="bp-btn bp-danger" onClick={handleStayUninsured}>
          <div className="bp-label">Stay uninsured — self-insure with savings</div>
          <div className="bp-meta">No premium. No claim. All risk on you.</div>
        </button>
      </div>
    </>
  );
};

// ============================================================
// IRA CHOICE
// ============================================================
const IRAChoiceScreen = ({ state, update }) => {
  const handleFile = () => {
    let upheldChance = 0.4;
    if (state.claimOutcome === 'denied_nondisclosure' && state.health !== 'clean' && state.discCondition === 'hide') upheldChance = 0.05;
    if (state.claimOutcome === 'denied_lapse') upheldChance = 0.15;
    if (state.claimOutcome === 'denied_exclusion') upheldChance = 0.35;
    const upheld = Math.random() < upheldChance;
    update({
      iraComplaintFiled: true,
      iraOutcome: upheld ? 'upheld' : 'denied',
      claimAmount: upheld ? Math.round(state.sumAssured * 0.5) : state.claimAmount,
      claimOutcome: upheld ? 'paid_via_ira' : state.claimOutcome,
      screen: 'ira_outcome',
    });
  };

  const denialReason =
    state.claimOutcome === 'denied_lapse' ? 'policy lapse (you missed premiums)' :
    state.claimOutcome === 'denied_nondisclosure' ? 'material non-disclosure (you hid information at application)' :
    state.claimOutcome === 'denied_exclusion' ? 'policy exclusion' : 'unknown reason';

  return (
    <>
      <div className="bp-card bp-ira">
        <span className="bp-pill bp-ira-pill">⚖ IRA CONSUMER PROTECTION</span>
        <h2 style={{ marginTop: 12 }}>You don't have to accept this</h2>
        <p>Your claim has been denied. Before you accept the insurer's decision as final, you should know something most Kenyans don't:</p>
        <div className="bp-ira-info">
          <p><strong>The Insurance Regulatory Authority (IRA)</strong> is a statutory body that regulates Kenyan insurers. If you believe an insurer has treated you unfairly — delayed your claim, denied without proper reason, missold you a product — you can lodge a complaint with IRA.</p>
          <span className="bp-ira-contact">Email: complaints@ira.go.ke<br />Office: Zep-Re Place, Longonot Road, Upper Hill, Nairobi<br />Cost: Free</span>
        </div>
        <p><strong>The process:</strong></p>
        <ol style={{ marginLeft: 20, fontSize: 14, color: 'var(--ink-mid)', lineHeight: 1.7 }}>
          <li>Write formally to the insurer's internal complaints unit. Give them 30 days.</li>
          <li>If unresolved, write to IRA. Include the insurer's response, your policy documents, claim correspondence.</li>
          <li>IRA investigates. They can interview both parties, examine evidence, and form an independent view.</li>
          <li>IRA can rule in your favour and order the insurer to pay, or uphold the insurer's decision with reasoning.</li>
        </ol>
        <p>This actually works. Real cases get overturned every month — particularly where insurers cite weak grounds for denial.</p>
      </div>
      <div className="bp-card">
        <h3>Do you want to file a complaint?</h3>
        <p>Your claim was denied for: <strong>{denialReason}</strong>.</p>
        <button className={`bp-btn ${state.claimOutcome === 'denied_lapse' || state.claimOutcome === 'denied_nondisclosure' ? 'bp-warn' : 'bp-good'}`} onClick={handleFile}>
          <div className="bp-label">File a complaint with IRA</div>
          <div className="bp-meta">Free, takes 2-4 months, sometimes overturned, sometimes upheld</div>
        </button>
        <button className="bp-btn" onClick={() => update({ screen: 'end' })}>
          <div className="bp-label">Accept the denial, move on</div>
          <div className="bp-meta">Pay the bill yourself or find another way</div>
        </button>
      </div>
    </>
  );
};

// ============================================================
// IRA OUTCOME
// ============================================================
const IRAOutcomeScreen = ({ state, update }) => {
  const { iraOutcome, claimAmount, sumAssured, claimOutcome } = state;
  return (
    <div className="bp-card bp-ira">
      <span className="bp-pill bp-ira-pill">⚖ IRA RULING</span>
      <h2 style={{ marginTop: 12 }}>{iraOutcome === 'upheld' ? 'Your complaint was upheld' : 'Your complaint was not upheld'}</h2>
      {iraOutcome === 'upheld' ? (
        <>
          <div className="bp-story ira">
            <strong>"Having reviewed the documentation submitted by both parties, the Authority finds that [Insurer] applied a standard of disclosure that exceeds what is reasonably expected of a lay applicant. The Authority directs the insurer to settle the claim in full per the policy schedule."</strong>
          </div>
          <p>Three weeks later, the insurer's cheque arrives. KSh {fmt(claimAmount)}. Treatment is now funded.</p>
          <p><strong>What you learned:</strong> The first denial is not the final word. Many denials are insurer first lines of defence — they hope you'll accept it. IRA is the safety net most policyholders don't know exists.</p>
        </>
      ) : (
        <>
          <div className="bp-story bad">
            <strong>"Having reviewed the documentation submitted by both parties, the Authority finds that the insurer acted within the terms of the policy and the Insurance Act. {claimOutcome === 'denied_nondisclosure' ? 'Material information was withheld at application. The denial is upheld.' : 'The exclusion cited is clearly stated in the policy schedule. The denial is upheld.'}"</strong>
          </div>
          <p>The denial stands. The bill is yours.</p>
          <p><strong>What you learned:</strong> IRA is genuinely independent. When the insurer is right, it says so. Lesson: the disclosure was the moment that mattered, not the claim.</p>
        </>
      )}
      <button className="bp-btn bp-primary" onClick={() => update({ screen: 'end' })}>See final results →</button>
    </div>
  );
};

// ============================================================
// END SCREEN
// ============================================================
const EndScreen = ({ state, reset }) => {
  const sa = state.sumAssured || 5000000;
  const base = { ...state, riderCritical: false, riderDisability: false };
  const termPMonthly = calculatePremium(base, 'term', sa);
  const wholePMonthly = calculatePremium(base, 'whole', sa);
  const endowPMonthly = calculatePremium(base, 'endow', sa);
  const monthlyDiff = wholePMonthly - termPMonthly;

  let mmfSim = 0;
  const monthlyRate = 0.1 / 12;
  for (let m = 0; m < 23 * 12; m++) mmfSim = mmfSim * (1 + monthlyRate) + monthlyDiff;
  const termInvestPortfolio = Math.round(mmfSim);
  const termInvestPremiums = termPMonthly * 12 * 23;

  const wholePremiums = wholePMonthly * 12 * 23;
  const wholeCashValue = Math.round(wholePremiums * 0.72);
  const endowPremiums = endowPMonthly * 12 * 20;
  const endowMaturity = Math.round(endowPremiums * 1.2);

  const totalRecovered = state.surrenderedAmount + state.claimAmount + state.cashValue + (state.product === 'term_invest' ? state.mmfBalance : 0);
  const netPosition = totalRecovered - state.totalPremiumsPaid;
  const insights = generateInsights(state);

  const compareRows = [
    ['Term life only', termPMonthly * 12 * 23, state.product === 'term' ? state.claimAmount : 0],
    ['Whole life', wholePremiums, wholeCashValue + (state.product === 'whole' ? state.claimAmount : 0)],
    ['Endowment', endowPremiums, endowMaturity + (state.product === 'endowment' ? state.claimAmount : 0)],
    ['Term + MMF', termInvestPremiums, termInvestPortfolio + (state.product === 'term_invest' ? state.claimAmount : 0)],
  ];

  const claimNote = state.claimOutcome
    ? state.claimOutcome === 'denied_lapse' ? 'Denied — policy was lapsed'
    : state.claimOutcome === 'denied_nondisclosure' ? 'Denied — non-disclosure'
    : state.claimOutcome === 'no_rider' ? 'No applicable rider'
    : state.claimOutcome === 'didnt_file' ? "You didn't file"
    : 'Denied'
    : 'No claim event';

  return (
    <>
      <h1 className="bp-display bp-title">Your <span className="bp-accent">25 years</span></h1>
      <p className="bp-tag bp-mono">Here's what happened to you, and what would have happened on each other path.</p>

      <div className="bp-end-scores">
        <div className="bp-end-score">
          <div className="bp-end-label">Total Premiums Paid</div>
          <div className="bp-end-value">{fmt(state.totalPremiumsPaid)}</div>
          <div className="bp-end-sub">KSh</div>
        </div>
        <div className="bp-end-score">
          <div className="bp-end-label">Total Recovered</div>
          <div className={`bp-end-value ${totalRecovered > 0 ? 'pos' : 'neg'}`}>{fmt(totalRecovered)}</div>
          <div className="bp-end-sub">claims + surrender + cash value</div>
        </div>
      </div>

      <div className="bp-card">
        <h3>Your full money trail</h3>
        <div className="bp-compare-row bp-head"><div>Item</div><div>Amount</div><div>Note</div><div /></div>
        <div className="bp-compare-row">
          <div>Premiums paid (original policy)</div>
          <div className="bp-v bp-neg">−{fmt(state.totalPremiumsPaid)}</div>
          <div>{state.lapsedAtYear ? `Paid until lapse at year ${state.lapsedAtYear}` : 'Paid throughout'}</div>
          <div />
        </div>
        {state.surrenderedAmount > 0 && (
          <>
            <div className="bp-compare-row">
              <div>Surrender value recovered</div>
              <div className="bp-v bp-pos">+{fmt(state.surrenderedAmount)}</div>
              <div>~{Math.round((state.surrenderedAmount / state.totalPremiumsPaid) * 100)}% of premiums paid</div>
              <div />
            </div>
            <div className="bp-compare-row">
              <div><em>Premiums forfeited via surrender</em></div>
              <div className="bp-v bp-neg"><em>−{fmt(state.totalPremiumsPaid - state.surrenderedAmount)}</em></div>
              <div><em>Money paid in but not returned</em></div>
              <div />
            </div>
          </>
        )}
        {state.cashValue > 0 && (
          <div className="bp-compare-row">
            <div>Cash value / maturity benefit</div>
            <div className="bp-v bp-pos">+{fmt(state.cashValue)}</div>
            <div>{state.product === 'whole' ? 'Whole life cash value at year 23' : 'Endowment maturity'}</div>
            <div />
          </div>
        )}
        {state.claimAmount > 0 ? (
          <div className="bp-compare-row">
            <div>Claim payout</div>
            <div className="bp-v bp-pos">+{fmt(state.claimAmount)}</div>
            <div>{state.claimOutcome === 'paid_via_ira' ? 'Won at IRA after initial denial' : 'CI/disability rider paid out'}</div>
            <div />
          </div>
        ) : (
          <div className="bp-compare-row">
            <div>Claim payout</div>
            <div className="bp-v bp-neg">0</div>
            <div>{claimNote}</div>
            <div />
          </div>
        )}
        {state.product === 'term_invest' && state.mmfBalance > 0 && (
          <div className="bp-compare-row">
            <div>MMF portfolio (parallel investment)</div>
            <div className="bp-v bp-pos">+{fmt(state.mmfBalance)}</div>
            <div>Term premium savings invested at 10% p.a.</div>
            <div />
          </div>
        )}
        <div className="bp-compare-row" style={{ borderTop: '2px solid var(--accent)', background: 'var(--gold-soft)' }}>
          <div><strong>Net position</strong></div>
          <div className={`bp-v ${netPosition >= 0 ? 'bp-pos' : 'bp-neg'}`}><strong>{netPosition >= 0 ? '+' : ''}{fmt(netPosition)}</strong></div>
          <div><strong>{netPosition >= 0 ? 'Came out ahead' : 'Net cost'}</strong></div>
          <div />
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-dim)', marginTop: 12 }}><em>Note: a negative net position is normal for insurance — you're paying for protection you (hopefully) don't need. The value of cover that paid out when needed is not captured by this number.</em></p>
      </div>

      {state.product === 'term_invest' && state.mmfBalance > 0 && (
        <div className="bp-card">
          <h3>MMF investment breakdown</h3>
          <p style={{ fontSize: 13 }}>You paid the cheap term-life premium and invested the difference (vs whole life premium) into a Money Market Fund at ~10% p.a.</p>
          <div className="bp-compare-row bp-head"><div>Metric</div><div>Value</div><div /><div /></div>
          <div className="bp-compare-row"><div>Monthly contribution to MMF</div><div>{fmt(monthlyDiff)}</div><div>= whole life premium − term premium</div><div /></div>
          <div className="bp-compare-row"><div>Total contributed over 23 years</div><div>{fmt(monthlyDiff * 12 * 23)}</div><div /><div /></div>
          <div className="bp-compare-row"><div>Final MMF balance</div><div className="bp-v bp-pos">{fmt(state.mmfBalance)}</div><div>With compound interest at 10% p.a.</div><div /></div>
          <div className="bp-compare-row"><div>Pure growth</div><div className="bp-v bp-pos">+{fmt(state.mmfBalance - monthlyDiff * 12 * 23)}</div><div>~{Math.round((state.mmfBalance / (monthlyDiff * 12 * 23) - 1) * 100)}% total return</div><div /></div>
        </div>
      )}

      <div className="bp-card">
        <h3>What you got vs the alternatives</h3>
        <p style={{ fontSize: 12, color: 'var(--ink-dim)' }}>(Same age, same disclosures, same claim event — different products)</p>
        <div className="bp-compare-row bp-head"><div>Path</div><div>Paid</div><div>Got back</div><div>Net</div></div>
        {compareRows.map(([label, paid, gotBack], i) => {
          const net = gotBack - paid;
          return (
            <div key={i} className="bp-compare-row">
              <div>{label}</div>
              <div>{fmt(paid)}</div>
              <div>{fmt(gotBack)}</div>
              <div className={`bp-v ${net >= 0 ? 'bp-pos' : 'bp-neg'}`}>{net >= 0 ? '+' : ''}{fmt(net)}</div>
            </div>
          );
        })}
        <p style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: 12 }}><em>Note: "Got back" excludes the protection value when there was no claim. Insurance is also about what would have been paid if the worst had happened.</em></p>
      </div>

      <div className="bp-card">
        <h2>What your year revealed</h2>
        {insights.map((ins, i) => (
          <div key={i} className={`bp-insight ${ins.tone}`}>
            <div className="bp-insight-head">{ins.headline}</div>
            <div className="bp-insight-body">{ins.body}</div>
          </div>
        ))}
      </div>

      <div className="bp-card bp-gold">
        <h3>The most important lesson</h3>
        <p>Insurance is not a product you buy and forget. It's a contract that pays out only when (a) you disclosed honestly at application, (b) the cause of loss isn't excluded, (c) the policy is still in force, and (d) the right rider was attached for the right event.</p>
        <p>And when an insurer says no and you believe they're wrong — <strong>complaints@ira.go.ke</strong>. Free. They actually rule against insurers when the case has merit.</p>
      </div>

      <button className="bp-btn bp-primary" onClick={reset}>Play again with different choices →</button>
      <p className="bp-footer-note">Bima Path • An education-first insurance simulator</p>
    </>
  );
};

// ============================================================
// ASSUMPTIONS DOC (collapsible on intro screen)
// ============================================================
const AssumptionsDoc = () => (
  <div className="bp-assumptions-body open">
    <div className="bp-doc">
      <p>This document sets out the modelling assumptions and parameters used in Bima Path. Numbers are calibrated to publicly observable Kenyan retail life insurance pricing as of 2024-2025, but are illustrative rather than insurer-specific.</p>

      <h3>1 — Premium calculation</h3>
      <p>Base monthly premiums are expressed per 1,000,000 KSh of sum assured for a healthy non-smoking applicant in an office occupation. Premiums for any age between the reference ages are linearly interpolated.</p>
      <table>
        <thead><tr><th>Age</th><th>Term (KES/M/mo)</th><th>Whole (KES/M/mo)</th><th>Endowment 20y</th></tr></thead>
        <tbody>
          {[[22,280,1100,3400],[25,320,1280,3520],[30,420,1600,3800],[35,580,2050,4180],[40,850,2580,4650],[45,1280,3200,5220],[50,1950,4100,6080],[55,2900,5300,7100],[60,4500,6800,8400],[65,7000,8800,9900]].map(([a,t,w,e]) => (
            <tr key={a}><td>{a}</td><td>{t.toLocaleString()}</td><td>{w.toLocaleString()}</td><td>{e.toLocaleString()}</td></tr>
          ))}
        </tbody>
      </table>

      <h3>2 — Loadings on base rates</h3>
      <ul>
        <li>Hazardous occupation honestly disclosed: ×1.35</li>
        <li>Minor pre-existing condition honestly disclosed: ×1.25</li>
        <li>Chronic condition honestly disclosed: ×1.60</li>
        <li>Conditional approval after AML/CFT enhanced due diligence: ×1.12</li>
      </ul>

      <h3>3 — Rider rates</h3>
      <ul>
        <li>Critical Illness rider: 380 per 1M / month</li>
        <li>Disability rider: 220 per 1M / month</li>
      </ul>

      <h3>4 — Cash surrender values</h3>
      <table>
        <thead><tr><th>Years in force</th><th>Whole Life</th><th>Endowment</th></tr></thead>
        <tbody>
          {[['< 2 years','0%','0%'],['2 – 5 years','20%','25%'],['5 – 10 years','45%','55%'],['10 – 15 years','60%','75%'],['15 – 20 years','72%','95%'],['20+ years','85%','115%']].map(([y,w,e]) => (
            <tr key={y}><td>{y}</td><td>{w}</td><td>{e}</td></tr>
          ))}
        </tbody>
      </table>

      <h3>5 — MMF investment counterfactual</h3>
      <p>For the "Term + Invest the Difference" path, monthly contribution to MMF equals (whole life premium − term life premium). Contributions are compounded monthly at 10% p.a. nominal (effective 10.47% annualized). This rate approximates the current Kenyan MMF average.</p>

      <h3>6 — Claim mechanics</h3>
      <ul>
        <li>Critical Illness rider payout: 50% of sum assured (accelerated benefit)</li>
        <li>Death benefit payout: 100% of sum assured</li>
        <li>Disability rider payout: 50% of sum assured plus future premium waiver</li>
        <li>CI rider waiting period: 6 months from policy inception</li>
        <li>Non-disclosure consequence: full policy void per Section 153 of the Insurance Act</li>
        <li>Grace period for lapse: 90 days from missed premium</li>
      </ul>

      <h3>7 — IRA complaint outcome probabilities (illustrative only)</h3>
      <ul>
        <li>Baseline upheld for the consumer (general exclusion disputes): 40%</li>
        <li>Non-disclosure cases with genuine concealment: 5%</li>
        <li>Lapse-based denials: 15%</li>
        <li>Other exclusion-based denials: 35%</li>
      </ul>

      <h3>8 — Modelling simplifications</h3>
      <ul>
        <li>All amounts expressed in nominal KES; no inflation, no discounting</li>
        <li>Tax treatment not modelled</li>
        <li>Each insurer carries product variations; this simulator is generic</li>
        <li>Real-world MMF returns net of withholding tax and fees would yield somewhat less</li>
        <li>Simulation horizon: 25 years compressed into 5 discrete events at years 3, 8, 13, 18, 23</li>
      </ul>

      <div className="bp-doc-version">BIMA PATH v2.0 • METHODOLOGY DOCUMENT • LAST REVIEWED 2024-Q4</div>
    </div>
  </div>
);

export default BimaPath;